/** Canonical, renderer-independent DataGlow format 1.0. */
export type UUID = string;
export type ModelType =
  "conceptual" | "logical" | "logical-physical" | "physical";
export type Family =
  "relational" | "semantic" | "analytical" | "document" | "graph" | "process";
export type Dialect = "postgresql" | "oracle" | "sqlserver" | "mysql" | "ase";
export type Side = "top" | "right" | "bottom" | "left";
export type Notation = "crow-foot" | "idef1x" | "uml" | "chen";
export interface Point {
  x: number;
  y: number;
}
export interface ExternalIdentity {
  connectorId: string;
  sourceSystemId: string;
  sourceObjectType: string;
  sourceObjectId?: string;
  qualifiedName: string[];
  observedAt: string;
  confidence: "authoritative" | "matched" | "inferred";
}
export interface TypeSpec {
  name: string;
  length?: number;
  precision?: number;
  scale?: number;
  raw?: string;
}
export interface DomainObject {
  objectId: UUID;
  kind:
    | "concept"
    | "entity"
    | "table"
    | "attribute"
    | "column"
    | "key"
    | "view"
    | "index"
    | "collection"
    | "property"
    | "semanticEntity"
    | "semanticField"
    | "measure"
    | "fact"
    | "dimension"
    | "hierarchy"
    | "domain"
    | "note"
    | "sequence"
    | "constraint";
  representationId: UUID;
  name: string;
  displayName?: string;
  description?: string;
  briefDescription?: string;
  ownerObjectId?: UUID;
  lifecycle: "active" | "deprecated" | "archived";
  tags: string[];
  classifications: string[];
  sourceIdentities: ExternalIdentity[];
  extensions: Record<string, unknown>;
  type?: TypeSpec;
  nullable?: boolean;
  defaultValue?: string;
  generated?: boolean;
  logicalType?: string;
  ordinal?: number;
  members?: UUID[];
  keyRole?: "primary" | "candidate" | "business" | "surrogate" | "unique";
  selectedPrimary?: boolean;
  schema?: string;
  sql?: string;
  expression?: string;
  unique?: boolean;
  checkExpression?: string;
  sourceRef?: {
    modelId: UUID;
    objectId: UUID;
    fieldId?: UUID;
    commitId?: UUID;
  };
  grain?: UUID[];
  aggregation?: "sum" | "count" | "distinct-count" | "avg" | "min" | "max";
  documentSchema?: Record<string, unknown>;
  physicalKeyStrategy?: "business-primary" | "surrogate-primary";
}
export interface Representation {
  representationId: UUID;
  kind: ModelType | "semantic" | "analytical" | "document";
  name: string;
  origin: "original" | "derived" | "imported";
  objectIds: UUID[];
  targetProfile?: Dialect;
  sourceRepresentationId?: UUID;
  baseline?: boolean;
  createdAt?: string;
}
export interface Relationship {
  relationshipId: UUID;
  name: string;
  representationId: UUID;
  sourceObjectId: UUID;
  targetObjectId: UUID;
  sourceCardinality: "one" | "many";
  targetCardinality: "one" | "many";
  sourceOptional: boolean;
  targetOptional: boolean;
  identifying: boolean;
  description?: string;
  sourceAttributeIds?: UUID[];
  targetAttributeIds?: UUID[];
  onDelete?: string;
  onUpdate?: string;
  extensions?: Record<string, unknown>;
}
export type ThemePreset = "light" | "dark" | "soft" | "colorful" | "monochrome" | "contrast" | "custom" | "system";
export type PaletteId = "teal" | "blue" | "violet" | "amber" | "rose" | "slate" | "green";
/** business/mandatory are retained Alpha 2.1 views; business-v2 requires explicit business classification. */
export type EntityDetailLevel = "name" | "primary" | "business" | "business-v2" | "mandatory" | "all";
export interface ThemeTokens {
  canvas: string;
  surface: string;
  header: string;
  text: string;
  muted: string;
  border: string;
  accent: string;
  relationship: string;
}
export interface RelationshipLabelStyle {
  orientation?: "horizontal" | "vertical" | "aligned";
  placement?: "above" | "below" | "on-line";
  offset?: number;
  position?: number;
  visible?: boolean;
  source?: "name" | "description";
}
export interface RelationshipStyle {
  color?: string;
  width?: number;
  lineStyle?: "solid" | "dashed" | "dotted";
  identifyingWidth?: number;
  label?: RelationshipLabelStyle;
}
export interface EntityDisplayOptions {
  primaryKey: boolean;
  businessKey: boolean;
  allAttributes: boolean;
  dataType: boolean;
  nullability: boolean;
  description: boolean;
}
export interface ModelAppearance {
  paletteId?: PaletteId;
  color?: string;
}
export interface DiagramAppearance extends ModelAppearance {
  detailLevel?: EntityDetailLevel;
  entityDisplay?: EntityDisplayOptions;
  relationship?: RelationshipStyle;
}
export interface Appearance {
  appearanceId: UUID;
  objectId: UUID;
  position: Point;
  width?: number;
  height?: number;
  pinned?: boolean;
  sizeLocked?: boolean;
  color?: string;
  collapsed?: boolean;
}
export interface Connector {
  relationshipId: UUID;
  sourceSide?: Side;
  targetSide?: Side;
  sourceSlot?: number;
  targetSlot?: number;
  /** Normalized attachment along the selected side, preserved for manual routes. */
  sourceFraction?: number;
  targetFraction?: number;
  waypoints?: Point[];
  routing?: "auto" | "manual";
  style?: RelationshipStyle;
}
/** Geometry-only bookmark. Historical IDs remain valid after an entity is removed. */
export interface LayoutCheckpoint {
  checkpointId: UUID;
  name: string;
  createdAt: string;
  positions: {
    appearanceId: UUID;
    objectId: UUID;
    position: Point;
    /** Bounds inform routing compatibility; restoration never replaces current sizes. */
    width?: number;
    height?: number;
  }[];
  connectors: (Omit<Connector, "style"> & {
    sourceObjectId?: UUID;
    targetObjectId?: UUID;
  })[];
}
export interface Diagram {
  diagramId: UUID;
  diagramVersion: number;
  name: string;
  /** Explicit ownership; omitted only by pre-2.5 documents. */
  role?: "main" | "subject-area" | "view";
  /** Only views may belong to a subject-area diagram in the same representation. */
  parentDiagramId?: UUID;
  roleMigration?: "legacy-single" | "legacy-preserved" | "legacy-recovered";
  perspective: Family | "conceptual" | "logical" | "physical";
  representationId: UUID;
  notation: Notation;
  appearances: Appearance[];
  connectors: Connector[];
  viewportDefault?: { x: number; y: number; zoom: number };
  layoutProfile?: Record<string, unknown>;
  appearance?: DiagramAppearance;
  layoutCheckpoints?: LayoutCheckpoint[];
}
export interface Mapping {
  mappingId: UUID;
  sourceRefs: { modelId: UUID; representationId: UUID; objectId: UUID }[];
  destinationRefs: { modelId: UUID; representationId: UUID; objectId: UUID }[];
  mappingKind: "equivalent" | "derived" | "split" | "merge" | "physical-only";
  ruleId?: string;
  ruleVersion?: string;
  origin: "declared" | "imported" | "inferred";
  reviewState: "unreviewed" | "accepted" | "rejected" | "needs-reconciliation";
  rationale: string;
}
export interface ModelDocument {
  format: "dataglow.model";
  formatVersion: "1.0";
  model: {
    modelId: UUID;
    name: string;
    family: Family;
    modelType: ModelType;
    activeRepresentation: UUID;
    description?: string;
    appearance?: ModelAppearance;
    targetProfile?: Dialect;
    originalConceptualBaseline?: UUID | null;
    namingPolicy?: {
      ruleSetId: string;
      version: number;
      strategy: "prospective" | "retrospective";
    };
  };
  representations: Representation[];
  objects: DomainObject[];
  relationships: Relationship[];
  diagrams: Diagram[];
  mappings: Mapping[];
  dependencies: Record<string, unknown>[];
  sourceIdentities: ExternalIdentity[];
  extensions: Record<string, unknown>;
  manifest: {
    producer: string;
    producerVersion: string;
    objectCount: number;
    relationshipCount: number;
    requiredExtensions: string[];
    optionalExtensions: string[];
  };
}
export interface Diagnostic {
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  objectId?: string;
  path?: string;
  line?: number;
  helpTopic?: string;
  /** Exact UTF-16 source span when supplied by a text importer. */
  sourceStart?: number;
  sourceEnd?: number;
}
export interface LossReport {
  preserved: string[];
  translated: string[];
  preservedOpaque: string[];
  lost: string[];
  unresolved: string[];
}
export interface Operation {
  type: string;
  object?: DomainObject;
  objectId?: UUID;
  changes?: Record<string, unknown>;
  relationship?: Relationship;
  relationshipId?: UUID;
  diagram?: Diagram;
  diagramId?: UUID;
  appearance?: Appearance;
  appearanceId?: UUID;
  representation?: Representation;
  mapping?: Mapping;
  document?: ModelDocument;
  [key: string]: unknown;
}
export interface Command {
  commandSchemaVersion: "1.0";
  commandId: UUID;
  modelId: UUID;
  branchId: UUID;
  baseRevision: number;
  baseCommitId?: UUID;
  operations: Operation[];
  summary?: string;
  undoOf?: UUID;
  redoOf?: UUID;
}
export interface ModelSnapshot {
  document: ModelDocument;
  revision: number;
  commitId: UUID;
  branchId: UUID;
  repositoryId: UUID;
  updatedAt: string;
  updatedBy: string;
}
export interface ModelSummary {
  modelId: UUID;
  name: string;
  family: Family;
  modelType: ModelType;
  repositoryId: UUID;
  branchId: UUID;
  revision: number;
  updatedAt: string;
  objectCount: number;
}
export interface Conflict {
  path: string;
  objectId?: string;
  original: unknown;
  local: unknown;
  incoming: unknown;
  message: string;
}
export interface CommandReceipt {
  commandId: UUID;
  commitId: UUID;
  revision: number;
  acceptedAt: string;
  rebased: boolean;
  snapshot: ModelSnapshot;
}
export type Role =
  | "platform-super-admin"
  | "tenant-super-admin"
  | "studio-admin"
  | "repository-admin"
  | "modeler"
  | "developer"
  | "viewer";
export interface Session {
  userId: UUID;
  name: string;
  email: string;
  tenantId: UUID;
  organizationCode: string;
  roles: Role[];
  permissions: string[];
  csrfToken?: string;
  branding: { name: string; accent: string };
}
export interface Repository {
  repositoryId: UUID;
  name: string;
  description?: string;
  mapping: { kind: "unmapped" | "database" | "schema"; name?: string };
}
export type ToolbarId = "commands" | "models" | "diagram" | "navigation" | "layout" | "export";
export type DockZone = "home" | "header" | "header-secondary" | "commands" | "tabs" | "diagram" | "top" | "bottom" | "left" | "right" | "floating";
export interface ToolbarPlacement { visible: boolean; zone: DockZone; x?: number; y?: number; order?: number }
export interface PaletteCommandPlacement { palette: Exclude<ToolbarId, "models">; order: number; alwaysShow: boolean }
export interface WorkspaceLayout { commands?: Record<string, PaletteCommandPlacement>; version?: 1; locked: boolean; toolbars: Partial<Record<ToolbarId, ToolbarPlacement>>; explorerWidth?: number; propertiesWidth?: number }
export interface ExportBackground { mode: "transparent" | "solid"; color: string; provenance?: "default" | "explicit" }
export type ModelTypePresentation = Partial<Record<"conceptual" | "logical" | "physical", Partial<Pick<Preferences, "fontSize" | "titleSize" | "detailLevel" | "entityDisplay" | "autoSizeEntities" | "resizeOnDetailChange" | "centerEntityNames" | "showEntityDescription">>>>;
export interface Preferences {
  theme: ThemePreset;
  accent: string;
  fontFamily: string;
  fontSize: number;
  titleSize: number;
  density: "comfortable" | "compact";
  settingsView: "icons" | "list";
  workspace: "balanced" | "focus" | "inspector-left";
  saveMode: "automatic" | "manual";
  /** 0 coalesces completed changes; positive values save on a fixed cadence. */
  autosaveIntervalSeconds: number;
  showAutosaveSuccess: boolean;
  receiveMode: "automatic" | "notify";
  autoAddId: boolean;
  autoAddDescription: boolean;
  nullabilityWording: "null" | "mandatory";
  logicalPrimaryKeyPolicy: "business-first" | "retain-source";
  removeSurrogatesOnDerive: boolean;
  railLabels: boolean;
  grid: boolean;
  snap: boolean;
  notation: Notation;
  experience: "guided" | "standard" | "expert";
  customTheme: ThemeTokens;
  paletteId: PaletteId;
  detailLevel: EntityDetailLevel;
  diagramDetailLevels: Record<string, EntityDetailLevel>;
  entityDisplay?: EntityDisplayOptions;
  diagramEntityDisplay?: Record<string, EntityDisplayOptions>;
  showAttributePropertiesOnSelection?: boolean;
  autoAdjustNearbyEntities?: boolean;
  autoAdjustRelationships?: boolean;
  layoutControls: "icons" | "compact";
  propertyPresentation: "popup" | "inspector";
  relationshipStyle: RelationshipStyle;
  workspaceLayout: WorkspaceLayout;
  personalWorkspaceLayout?: WorkspaceLayout | null;
  interfaceScale?: number;
  exportBackgrounds: { png: ExportBackground; svg: ExportBackground };
  modelTypePresentation: ModelTypePresentation;
  startupInterfaceSize: "small" | "medium" | "large";
  relationshipCreationMode: "endpoints" | "dialog";
  layoutPreviewMode: "single" | "all";
  showRelationshipNames: boolean;
  showMinimap?: boolean;
  preserveManualRoutes: boolean;
  /** True means the Explorer section is expanded. */
  explorerSections: { models: boolean; diagrams: boolean; objects: boolean; relationships?: boolean; relationshipsChoice?: "expanded" | "collapsed" };
  attributeDisplayLimit: number;
  showAllAttributes: boolean;
  allowUnlimitedCanvasExpansion: boolean;
  centerRelationshipEndpoints: boolean;
  avoidParentTopAttachment: boolean;
  relationshipLaneGap: number;
  autoSizeEntities: boolean;
  resizeOnDetailChange: boolean;
  centerEntityNames: boolean;
  showEntityDescription: boolean;
  entityPreviewEnabled: boolean;
  entityPreviewDelay: number;
}
export const defaultPreferences: Preferences = {
  theme: "light",
  accent: "#256d62",
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: 13,
  titleSize: 14,
  density: "comfortable",
  settingsView: "icons",
  workspace: "balanced",
  saveMode: "manual",
  autosaveIntervalSeconds: 0,
  showAutosaveSuccess: false,
  receiveMode: "notify",
  autoAddId: true,
  autoAddDescription: true,
  nullabilityWording: "null",
  logicalPrimaryKeyPolicy: "business-first",
  removeSurrogatesOnDerive: false,
  railLabels: true,
  grid: true,
  snap: true,
  notation: "crow-foot",
  experience: "standard",
  customTheme: { canvas: "#f3f6fa", surface: "#ffffff", header: "#e8edf5", text: "#172438", muted: "#5d6d82", border: "#c8d3e2", accent: "#2563eb", relationship: "#60758f" },
  paletteId: "teal",
  detailLevel: "all",
  diagramDetailLevels: {},
  showAttributePropertiesOnSelection: true,
  autoAdjustNearbyEntities: true,
  autoAdjustRelationships: true,
  layoutControls: "icons",
  propertyPresentation: "popup",
  workspaceLayout: { version: 1, locked: true, toolbars: {
    commands: { visible: true, zone: "home" }, models: { visible: true, zone: "home" },
    diagram: { visible: true, zone: "commands", order: 0 }, navigation: { visible: true, zone: "commands", order: 1 },
    layout: { visible: true, zone: "header-secondary", order: 0 }, export: { visible: true, zone: "header-secondary", order: 1 },
  } },
  exportBackgrounds: { png: { mode: "transparent", color: "#ffffff", provenance: "default" }, svg: { mode: "transparent", color: "#ffffff", provenance: "default" } },
  modelTypePresentation: { conceptual: { detailLevel: "name", showEntityDescription: true } },
  startupInterfaceSize: "medium",
  relationshipCreationMode: "endpoints",
  layoutPreviewMode: "single",
  showRelationshipNames: true,
  preserveManualRoutes: true,
  explorerSections: { models: true, diagrams: true, objects: true, relationships: false },
  attributeDisplayLimit: 20,
  showAllAttributes: false,
  allowUnlimitedCanvasExpansion: false,
  centerRelationshipEndpoints: true,
  avoidParentTopAttachment: true,
  relationshipLaneGap: 18,
  autoSizeEntities: true,
  resizeOnDetailChange: false,
  centerEntityNames: true,
  showEntityDescription: false,
  entityPreviewEnabled: true,
  entityPreviewDelay: 3000,
  relationshipStyle: { width: 1.7, label: { orientation: "aligned", placement: "above", offset: 12, position: 0.5, visible: true } },
};
export interface NamingRule {
  ruleSetId: string;
  name: string;
  version: number;
  case:
    | "preserve"
    | "snake_case"
    | "camelCase"
    | "PascalCase"
    | "UPPER_SNAKE"
    | "Title Case";
  tablePrefix: string;
  columnPrefix: string;
  surrogateSuffix: string;
  businessSuffix: string;
  abbreviations: Record<string, string>;
  maxLength: number;
}
export interface BuildingBlock {
  group?: "industry" | "corporate" | "foundation";
  industry?: string;
  tags?: string[];
  sourceReferences?: { title: string; url: string }[];
  assumptions?: string[];
  id: string;
  name: string;
  category: string;
  description: string;
  version: string;
  dependencies: string[];
  entityCount: number;
  create: (modelType: ModelType, dialect?: Dialect) => ModelDocument;
}
export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  summary: string;
  steps: string[];
  fields?: { name: string; description: string; example?: string }[];
  troubleshooting?: { symptom: string; resolution: string }[];
  related: string[];
}
export interface Capability {
  id: string;
  name: string;
  status: "implemented" | "experimental" | "blocked" | "planned";
  description: string;
  limitations: string[];
}
